import { BaseModel, type RelationConfig } from './base';

// Enums

export enum BillingMethodType {
  card = 'card',
  paypal = 'paypal',
}

export const BillingMethodTypeLabels: Record<BillingMethodType, string> = {
  [BillingMethodType.card]: 'api.models.billing.card',
  [BillingMethodType.paypal]: 'billing.paypal',
};

export enum BillingSubscriptionState {
  trial = 'trial',
  active = 'active',
  warning = 'warning',
  paymentError = 'payment-error',
  cancelled = 'cancelled',
  suspended = 'suspended',
  inactive = 'inactive',
  prepaid = 'prepaid',
  temporary = 'temporary',
}

export const BillingSubscriptionStateLabels: Record<BillingSubscriptionState, string> = {
  [BillingSubscriptionState.trial]: 'general.trial',
  [BillingSubscriptionState.active]: 'general.active',
  [BillingSubscriptionState.warning]: 'general.warning',
  [BillingSubscriptionState.paymentError]: 'general.paymentError',
  [BillingSubscriptionState.cancelled]: 'general.cancelled',
  [BillingSubscriptionState.suspended]: 'general.suspended',
  [BillingSubscriptionState.inactive]: 'general.inactive',
  [BillingSubscriptionState.prepaid]: 'api.models.billing.prepaid',
  [BillingSubscriptionState.temporary]: 'general.temporary',
};

export enum BillingPaymentType {
  plan = 0,
  credit = 1,
  auto = 2,
}

export const BillingPaymentTypeLabels: Record<BillingPaymentType, string> = {
  [BillingPaymentType.plan]: 'api.models.billing.subscriptionPayment',
  [BillingPaymentType.credit]: 'api.models.billing.smsCredit',
  [BillingPaymentType.auto]: 'api.models.billing.smsCreditAuto',
};

export enum BillingPaymentProcessor {
  braintree = 'braintree',
  paypal = 'paypal',
}

export const BillingPaymentProcessorLabels: Record<BillingPaymentProcessor, string> = {
  [BillingPaymentProcessor.braintree]: 'api.models.billing.braintree',
  [BillingPaymentProcessor.paypal]: 'billing.paypal',
};

export enum BillingPlanType {
  classic = 0,
  next = 1,
}

export const BillingPlanTypeLabels: Record<BillingPlanType, string> = {
  [BillingPlanType.classic]: 'api.models.billing.classic',
  [BillingPlanType.next]: 'api.models.billing.next',
};

export enum BillingPlanDiscountType {
  percentage = 1,
  fixed = 2,
}

export const BillingPlanDiscountTypeLabels: Record<BillingPlanDiscountType, string> = {
  [BillingPlanDiscountType.percentage]: 'general.percentage',
  [BillingPlanDiscountType.fixed]: 'general.fixed',
};

// Helper Classes

export class BillingPlanDiscount extends BaseModel {
  amountType!: BillingPlanDiscountType;
  amount!: number;

  get isAmountTypeFixed(): boolean {
    return this.amountType === BillingPlanDiscountType.fixed;
  }

  get isAmountTypePercentage(): boolean {
    return this.amountType === BillingPlanDiscountType.percentage;
  }
}

export class BillingCurrentPlan extends BaseModel {
  planDiscount!: BillingPlanDiscount | null;

  static override relations(): RelationConfig {
    return {
      planDiscount: { type: BillingPlanDiscount },
    };
  }
}

export class BillingPlan extends BaseModel {
  type!: BillingPlanType;

  get isPlanTypeClassic(): boolean {
    return this.type === BillingPlanType.classic;
  }

  get isPlanTypeNext(): boolean {
    return this.type === BillingPlanType.next;
  }
}

export class BillingPayment extends BaseModel {
  itemType!: BillingPaymentType;
  processor!: BillingPaymentProcessor;
  gross!: number;
  taxAmount!: number;
  currency!: string;

  get processorLabel(): string {
    if (this.processor === BillingPaymentProcessor.braintree) {
      return 'api.models.billing.creditCard';
    }
    if (this.processor === BillingPaymentProcessor.paypal) {
      return 'billing.paypal';
    }
    return 'general.payment';
  }

  get net(): string {
    return ((100 * this.gross) / (100 + this.taxAmount)).toFixed(2);
  }

  get tax(): string {
    return (this.gross - Number(this.net)).toFixed(2);
  }

  get priceLabel(): string {
    return `${this.currency} ${Number(this.gross).toFixed(2)}`;
  }

  get netLabel(): string {
    return `${this.currency} ${this.net}`;
  }

  get taxLabel(): string {
    return `${this.currency} ${this.tax}`;
  }

  get priceWithoutTaxLabel(): string {
    return this.taxAmount ? this.netLabel : this.priceLabel;
  }
}

export class BillingPayments extends BaseModel {
  payments!: BillingPayment[];

  static override relations(): RelationConfig {
    return {
      payments: { type: BillingPayment },
    };
  }
}

export class BillingMethod extends BaseModel {
  type!: BillingMethodType;
  token!: string;

  get isBillingMethodTypeCard(): boolean {
    return this.type === BillingMethodType.card;
  }

  get isBillingMethodTypePaypal(): boolean {
    return this.type === BillingMethodType.paypal;
  }
}

export class BillingStatus extends BaseModel {
  state!: BillingSubscriptionState;
  plan!: BillingPlan | null;
  currentLocationPlan!: BillingCurrentPlan | null;

  static override relations(): RelationConfig {
    return {
      plan: { type: BillingPlan },
      currentLocationPlan: { type: BillingCurrentPlan },
    };
  }

  get isBillingSubscriptionStateTrial(): boolean { return this.state === BillingSubscriptionState.trial; }
  get isBillingSubscriptionStateActive(): boolean { return this.state === BillingSubscriptionState.active; }
  get isBillingSubscriptionStateWarning(): boolean { return this.state === BillingSubscriptionState.warning; }
  get isBillingSubscriptionStatePaymentError(): boolean { return this.state === BillingSubscriptionState.paymentError; }
  get isBillingSubscriptionStateCancelled(): boolean { return this.state === BillingSubscriptionState.cancelled; }
  get isBillingSubscriptionStateSuspended(): boolean { return this.state === BillingSubscriptionState.suspended; }
  get isBillingSubscriptionStateInactive(): boolean { return this.state === BillingSubscriptionState.inactive; }
  get isBillingSubscriptionStatePrepaid(): boolean { return this.state === BillingSubscriptionState.prepaid; }
  get isBillingSubscriptionStateTemporary(): boolean { return this.state === BillingSubscriptionState.temporary; }
}

// Billing is not a model itself in the new system — it's just a service namespace
export class Billing extends BaseModel {}
